/**
 * Setters used by the monitoring/run-detail views to reset agent, scope,
 * and tool-action state whenever the "current selection" changes (new run,
 * new execution, brand-new streaming run).
 *
 * Extracted from `WorkflowDesignerWithCode` where the same 5-8 setter clears
 * appeared inline at five call sites. Consolidating here removes the DRY
 * violation, makes the ordering explicit, and lets us unit-test the reset
 * contract without spinning up the whole designer.
 *
 * Types are widened to `(value: unknown) => void` because each setter comes
 * back from a different `useState<T>` with a distinct `T`; carrying the exact
 * type through the boundary would require ten generic parameters here.
 * At call sites in `WorkflowDesignerWithCode`, TypeScript still enforces
 * the correct shape at the `useState` declarations.
 */
export interface RunViewStateSetters {
  setAgentIterations: (v: Record<string, number>) => void;
  setScopeIterations: (v: Record<string, number>) => void;
  setToolActions: (v: Map<string, unknown>) => void;
  setPendingToolActionFetches: (v: Set<string>) => void;
  setToolActionFetchQueue: (v: unknown[]) => void;
  setToolActionFetchRequest: (v: unknown) => void;
  setScopeRepetitions: (v: Map<string, unknown>) => void;
  setPendingScopeRepetitionFetches: (v: Set<string>) => void;
  setSelectedScopeRepetitions: (v: Record<string, string>) => void;
  setScopeRepetitionFetchRequest: (v: string | null) => void;
}

export interface RunViewStateResetters {
  /**
   * Reset for a brand-new run — clears iterations, tool actions, and fetch
   * queues. Used when the URL points at a different runId, on trigger success,
   * and when streaming completes and produces a real runId.
   */
  resetForNewRun: () => void;
  /**
   * Reset for a new execution within the same run — clears iteration + tool
   * state but leaves the fetch *queue* alone (queue is a run-level concept,
   * not execution-level).
   */
  resetForNewExecution: () => void;
}

export function buildRunViewStateResetters(
  setters: RunViewStateSetters
): RunViewStateResetters {
  const resetForNewRun = (): void => {
    setters.setAgentIterations({});
    setters.setScopeIterations({});
    setters.setToolActions(new Map());
    setters.setPendingToolActionFetches(new Set());
    setters.setToolActionFetchQueue([]);
    setters.setToolActionFetchRequest(null);
    setters.setScopeRepetitions(new Map());
    setters.setPendingScopeRepetitionFetches(new Set());
    setters.setSelectedScopeRepetitions({});
    setters.setScopeRepetitionFetchRequest(null);
  };

  const resetForNewExecution = (): void => {
    setters.setAgentIterations({});
    setters.setScopeIterations({});
    setters.setToolActions(new Map());
    setters.setPendingToolActionFetches(new Set());
    setters.setToolActionFetchRequest(null);
    setters.setScopeRepetitions(new Map());
    setters.setPendingScopeRepetitionFetches(new Set());
    setters.setSelectedScopeRepetitions({});
    setters.setScopeRepetitionFetchRequest(null);
  };

  return { resetForNewRun, resetForNewExecution };
}
